import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { inr } from "@/lib/format";

function Card({ label, value, hint, tone = "text-slate-900", testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={testid}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold tabular ${tone}`}>{value}</p>
      {hint && <p className="mt-0.5 text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
}

function Services({ items }) {
  const max = Math.max(1, ...items.map((s) => s.count));
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="booking-summary-services">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Bookings by service</p>
      {items.length === 0 ? <p className="mt-2 text-xs text-slate-400">No active bookings in this period.</p> : items.map((s) => (
        <div key={s.name} className="mt-2" data-testid={`booking-service-${s.name}`}>
          <div className="flex justify-between text-xs"><span className="truncate text-slate-700">{s.name}</span><span className="font-semibold tabular">{s.count}</span></div>
          <div className="mt-1 h-1.5 rounded-full bg-slate-100"><div className="h-1.5 rounded-full bg-sky-500" style={{ width: `${(s.count / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

export function BookingSummary({ period, start, end, basis }) {
  const { data: s, isError } = useQuery({
    queryKey: ["bookings-summary", period, start, end, basis], enabled: !!period,
    queryFn: async () => (await api.get("/bookings/summary", { params: { period, start, end, date_basis: basis } })).data,
  });
  if (!period) return null;
  if (isError) return <p className="mb-4 text-xs text-slate-400" data-testid="booking-summary-error">Summary unavailable right now. The booking list still works.</p>;
  if (!s) return <div className="mb-4 h-24 animate-pulse rounded-xl bg-slate-100" data-testid="booking-summary-loading" />;
  const r = s.revenue || {};
  return (
    <div className="mb-4 space-y-3" data-testid="booking-summary">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Card label={basis === "created" ? "Booked in period" : "Total bookings"} value={s.total} testid="booking-summary-total" />
        <Card label="Repeat" value={s.repeat_bookings} hint={`${s.returning_customers} returning customers`} tone="text-sky-700" testid="booking-summary-repeat" />
        <Card label="First-time" value={s.first_time_bookings} hint={s.unclassified_bookings ? `${s.unclassified_bookings} not classified` : `${s.first_time_customers} new customers`} tone="text-emerald-700" testid="booking-summary-first" />
        <Card label="Upcoming" value={s.upcoming} testid="booking-summary-upcoming" />
        <Card label="Completed" value={s.completed} tone="text-emerald-700" testid="booking-summary-completed" />
        <Card label="Cancelled / No-show" value={s.cancelled + s.no_show} hint={`${s.cancelled} cancelled · ${s.no_show} no-show`} tone="text-rose-600" testid="booking-summary-cancelled" />
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <div className="md:col-span-2"><Services items={s.services || []} /></div>
        <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="booking-summary-revenue">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Invoiced revenue</p>
          {r.available ? <>
            <p className="mt-1 text-2xl font-bold tabular text-slate-900" data-testid="booking-summary-revenue-net">{inr(r.net)}</p>
            <p className="mt-0.5 text-[11px] text-slate-400">{r.invoice_count} invoices linked to these bookings · before GST (GST {inr(r.gst)})</p>
          </> : <p className="mt-2 text-xs text-slate-400" data-testid="booking-summary-revenue-unavailable">Unavailable{r.reason ? ` — ${r.reason}` : ""}</p>}
        </div>
      </div>
    </div>
  );
}
